import type { FetchOptions } from 'ofetch'
import type { Composer } from 'vue-i18n'
import type { StrictLocaleObject } from '~~/lib/locales'
import { defineNuxtPlugin } from '#imports'
import { getProxyRequestHeaders } from 'h3'
import { CACHE_TAG_CONTEXT_KEY, CACHE_TAG_HEADER, htmlTagsForResponse } from '~~/lib/htmlCacheTags'
import { localeRequestParams } from '~~/lib/localeRequestParams'

export default defineNuxtPlugin({
  name: 'graphql-config',
  dependsOn: ['nuxt-graphql-middleware-provide-state', 'i18n:plugin', 'i18n:plugin:route-locale-detect'],
  setup(nuxtApp) {
    const state = useGraphqlState()

    if (!state)
      throw new Error('GraphQL state not available.')

    const event = useRequestEvent()

    const i18n = nuxtApp.$i18n as Composer | undefined
    const currentLocale = i18n?.locale?.value
    const locales = i18n?.locales?.value as unknown as StrictLocaleObject[] | undefined
    const currentLocaleObject = currentLocale && locales ? locales.find(l => l.code === currentLocale) as StrictLocaleObject | undefined : null

    const { localeCode, languageCode, currencyCode } = localeRequestParams(currentLocaleObject)

    const headers = {
      ...(event ? getProxyRequestHeaders(event) : {}),
      'X-Locale-Code-Cookie': currentLocale ?? 'en',
      'X-Locale-Code': localeCode,
      'X-Language-Code': languageCode,
      'X-Currency-Code': currencyCode,
      // Which side issued the call, consumed and stripped in
      // server/utils/makeGraphqlRequest.ts and never forwarded to Vendure.
      //
      // This plugin is the only place that can tell the two apart:
      // `useRequestEvent()` returns the page's H3 event during SSR and undefined
      // in the browser. Header sniffing cannot substitute, because the spread
      // above copies the viewer's headers onto in-process SSR calls, so an SSR
      // call looks exactly like a browser call at the header level.
      //
      // Stamped after the spread so an inbound viewer header can never
      // masquerade as `server`. Note `fetchOptions` is built once per app instance,
      // which is correct here: a given instance is either a server render or a
      // browser session for its whole life. Anything route-dependent would go
      // stale on the client after the first navigation, which is why `route`
      // below is only stamped during SSR.
      'X-Render-Origin': event ? 'server' : 'client',
      ...(event ? { 'X-Render-Route': (event.path ?? '').split('?')[0] } : {}),
    }

    state.fetchOptions = {
      headers,
      onRequest: (context) => {
        context.options.params ??= {}
        context.options.params.languageCode = languageCode
        context.options.params.currencyCode = currencyCode
        context.options.params.localeCode = localeCode
      },
      onResponse: (context) => {
        // Collect the cache tags of every GraphQL response that goes into this
        // render, so the HTML can carry the union and be purged by the same
        // events that purge the API responses behind it.
        //
        // Server only: `event` is undefined in the browser, and a client fetch
        // after hydration has nothing to tag. There is no second walk of the
        // payload here; the API response already did that work and returned the
        // answer in a header.
        if (!event)
          return

        const header = context.response?.headers?.get(CACHE_TAG_HEADER)
        if (!header)
          return

        const tags = header.split(',').map(t => t.trim()).filter(Boolean)
        const collected: Set<string> = (event.context[CACHE_TAG_CONTEXT_KEY] ??= new Set())

        // The nav collapses to one `nav:` tag, the placed outfits to their `op:`
        // tag and the announcement bar to its channel's `ab:` tag; everything else
        // keeps its tags. See htmlCacheTags.ts for why.
        for (const tag of htmlTagsForResponse(tags, localeCode)) collected.add(tag)
      },
    } satisfies FetchOptions
  },
})
